import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { InputTextComponent } from '../input-text/input-text.component';

/**
 * Searchable multi-select: a typeahead text trigger over an invisible
 * mat-select overlay, with +/- icons per option and a no-results row.
 * Extracted from the store-offer item-groups picker so the pattern is
 * reusable (item groups, option groups, …).
 *
 * The search term lives inside the component; filtering matches on
 * `labelKey`. Selection state stays with the parent: pass `[selectedValues]`
 * and react to `(selectionChange)`. `(opened)`/`(closed)` fire around a
 * selection session (the parent can snapshot/commit there).
 *
 * The template intentionally keeps the `item-group-*` class names and accepts
 * the historical element ids, so the existing global styling in
 * src/scss/dropdown.scss keeps applying unchanged.
 */
@Component({
  selector: 'app-searchable-multi-select',
  templateUrl: './searchable-multi-select.component.html',
  styleUrls: ['./searchable-multi-select.component.scss'],
})
export class SearchableMultiSelectComponent implements AfterViewInit {
  @Input() options: any[] = [];
  @Input() valueKey = 'value';
  @Input() labelKey = 'label';
  @Input() selectedValues: any[] = [];
  @Input() placeholder = '';
  /** Translation key for the disabled "no results" option. */
  @Input() noResultsLabel = '';
  @Input() panelClass = 'item-group-dropdown-panel';
  /** Historical ids so existing global #id styling keeps applying. */
  @Input() inputId = '';
  @Input() triggerId = '';
  @Input() triggerInputId = '';
  @Input() arrowId = '';

  /** Emits the new value array (like mat-select's $event.value). */
  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('searchInput')
  searchInput: InputTextComponent;
  @ViewChild('overlaySelect') overlaySelect: MatSelect;

  searchTerm = '';

  ngAfterViewInit(): void {
    // The base primitive has no click output; keep the historical
    // stopPropagation so a click on the input doesn't re-hit the trigger.
    this.searchInput?.bareInput?.nativeElement.addEventListener(
      'click',
      (event: MouseEvent) => event.stopPropagation(),
    );
  }

  get filteredOptions(): any[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.options;
    }
    return this.options.filter((option) =>
      String(this.optLabel(option) || '')
        .toLowerCase()
        .includes(term),
    );
  }

  get noResults(): boolean {
    return !!this.searchTerm.trim() && this.filteredOptions.length === 0;
  }

  /**
   * Track options by value: while a search term is typed, `filteredOptions`
   * builds a fresh array every change-detection pass; without trackBy, ngFor
   * would recreate every mat-option per pass and mat-select's async selection
   * re-init would schedule another pass — an infinite loop that freezes the page.
   */
  trackByValue = (_index: number, option: any): any => this.optValue(option);

  optValue(option: any): any {
    return option?.[this.valueKey];
  }

  optLabel(option: any): any {
    return option?.[this.labelKey];
  }

  isSelected(value: any): boolean {
    return (this.selectedValues || []).includes(value);
  }

  openPanel(): void {
    if (this.overlaySelect && !this.overlaySelect.panelOpen) {
      this.overlaySelect.open();
    }
  }

  onSearchInput(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value || '';
    this.openPanel();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
  }

  onOpened(): void {
    this.searchTerm = '';
    setTimeout(() => {
      this.searchInput?.bareInput?.nativeElement?.focus();
    }, 0);
    this.opened.emit();
  }

  onClosed(): void {
    this.searchTerm = '';
    this.closed.emit();
  }

  onSelectionChange(values: any[]): void {
    this.selectionChange.emit(values);
  }
}
